import { css } from '@emotion/react';

import { colors } from './colors';
import { columnWidths } from './columnWidths';
import { drawerWidths } from './drawerWidths';
import { spacings } from './spacings';
import { fonts } from './styled-theme';

const text = {
  familyBody: 'var(--fr-font-family-body)',
  sizeBodyXS: 'var(--fr-font-size-body-xs)',
  lineHeightBodyXS: 'var(--fr-font-line-height-body-xs)',
  sizeBodySM: 'var(--fr-font-size-body-sm)',
  sizeBodyMD: 'var(--fr-font-size-body-md)',
  lineHeightBodySM: 'var(--fr-font-line-height-body-sm)',
  sizeBodyLG: 'var(--fr-font-size-body-large)',
  lineHeightBodyMD: 'var(--fr-font-line-height-body-md)',
  sizeBodyXL: 'var(--fr-font-size-body-xl)',
  lineHeightBodyLG: 'var(--fr-font-line-height-body-lg)',
  familyHeading: 'var(--fr-font-family-heading)',
  sizeHeadingXXL: 'var(--fr-font-size-heading-xxl)',
  lineHeightHeadingXXL: 'var(--fr-font-line-height-heading-xxl)',
  letterSpacingXS: 'var(--fr-font-letter-spacing-xs)',
  sizeHeadingXL: 'var(--fr-font-size-heading-xl)',
  lineHeightHeadingXL: 'var(--fr-font-line-height-heading-xl)',
  sizeHeadingLG: 'var(--fr-font-size-heading-large)',
  lineHeightHeadingLG: 'var(--fr-font-line-height-heading-lg)',
  sizeHeadingMD: 'var(--fr-font-size-heading-medium)',
  lineHeightHeadingMD: 'var(--fr-font-line-height-heading-md)',
  sizeHeadingSM: 'var(--fr-font-size-heading-small)',
  lineHeightHeadingSM: 'var(--fr-font-line-height-heading-sm)',
  sizeHeadingXS: 'var(--fr-font-size-heading-xs)',
  lineHeightHeadingXS: 'var(--fr-font-line-height-heading-xs)',
  letterSpacingSM: 'var(--fr-font-letter-spacing-sm)',
};

export const themeVars = {
  fonts,
  text,
  colors,
  columnWidths,
  drawerWidths,
  spacings,
} as const;

export const themeValues: Record<keyof typeof themeVars.text, string> = {
  familyBody: 'Telka, sans-serif',
  sizeBodyXS: '12px',
  lineHeightBodyXS: '16px',
  sizeBodySM: '13px',
  sizeBodyMD: '14px',
  lineHeightBodySM: '20px',
  sizeBodyLG: '16px',
  lineHeightBodyMD: '24px',
  sizeBodyXL: '18px',
  lineHeightBodyLG: '28px',
  familyHeading: 'Telka Extended, sans-serif',
  sizeHeadingXXL: '56px',
  lineHeightHeadingXXL: '72px',
  letterSpacingXS: '0.24px',
  sizeHeadingXL: '48px',
  lineHeightHeadingXL: '64px',
  sizeHeadingLG: '40px',
  lineHeightHeadingLG: '56px',
  sizeHeadingMD: '32px',
  lineHeightHeadingMD: '40px',
  sizeHeadingSM: '24px',
  lineHeightHeadingSM: '32px',
  sizeHeadingXS: '20px',
  lineHeightHeadingXS: '28px',
  letterSpacingSM: '0.48px',
};

export const themeStyles = {
  text: {
    ellipsis: css`
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      min-width: 0;
      max-width: 100%;
    `,
    reset: css`
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      font-weight: normal;
      letter-spacing: normal;
      margin: 0;
      padding: 0;
      text-transform: none;
    `,
    telka: {
      body: {
        xs: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyXS};
          line-height: ${themeVars.text.lineHeightBodyXS};
          letter-spacing: 0;
        `,
        sm: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodySM};
          line-height: ${themeVars.text.lineHeightBodyXS};
          letter-spacing: 0;
        `,
        md: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyMD};
          line-height: ${themeVars.text.lineHeightBodySM};
          letter-spacing: 0;
        `,
        lg: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyLG};
          line-height: ${themeVars.text.lineHeightBodyMD};
          letter-spacing: 0;
        `,
        xl: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyXL};
          line-height: ${themeVars.text.lineHeightBodyLG};
          letter-spacing: 0;
        `,
        xsBold: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyXS};
          line-height: ${themeVars.text.sizeBodyXS};
          letter-spacing: 0;
        `,
        smBold: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodySM};
          line-height: ${themeVars.text.sizeBodyXS};
          letter-spacing: 0;
        `,
        mdBold: css`
          font-family: ${themeVars.text.familyBody};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyMD};
          line-height: ${themeVars.text.lineHeightBodySM};
          letter-spacing: 0;
        `,
      },
    },
    telkaEx: {
      display: css`
        font-family: 'Telka Extended';
        font-weight: 400;
        font-size: 72px;
        line-height: 130%;
        letter-spacing: 0;
      `,
      head: {
        xxl: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingXXL};
          line-height: ${themeVars.text.lineHeightHeadingXXL};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        xl: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingXL};
          line-height: ${themeVars.text.lineHeightHeadingXL};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        lg: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingLG};
          line-height: ${themeVars.text.lineHeightHeadingLG};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        md: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingMD};
          line-height: ${themeVars.text.lineHeightHeadingMD};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        sm: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingSM};
          line-height: ${themeVars.text.lineHeightHeadingSM};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        xs: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeHeadingXS};
          line-height: ${themeVars.text.lineHeightHeadingXS};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
      },

      body: {
        xlBold: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyXL};
          line-height: ${themeVars.text.lineHeightBodyLG};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        xl: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyXL};
          line-height: ${themeVars.text.lineHeightBodyLG};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        lgBold: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyLG};
          line-height: ${themeVars.text.lineHeightBodyMD};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        lg: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 400;
          font-size: 16px;
          line-height: ${themeVars.text.lineHeightBodyMD};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        mdBold: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyMD};
          line-height: ${themeVars.text.lineHeightBodySM};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        md: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 400;
          font-size: ${themeVars.text.sizeBodyMD};
          line-height: ${themeVars.text.lineHeightBodySM};
          letter-spacing: ${themeVars.text.letterSpacingXS};
        `,
        sm: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodySM};
          line-height: ${themeVars.text.sizeBodyXS};
          letter-spacing: ${themeVars.text.letterSpacingSM};
          text-transform: uppercase;
        `,
        xs: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyXS};
          line-height: ${themeVars.text.sizeBodyXS};
          letter-spacing: ${themeVars.text.letterSpacingSM};
          text-transform: uppercase;
        `,
        xsLow: css`
          font-family: ${themeVars.text.familyHeading};
          font-weight: 500;
          font-size: ${themeVars.text.sizeBodyXS};
          line-height: ${themeVars.text.lineHeightBodyXS};
          letter-spacing: ${themeVars.text.letterSpacingXS};
          text-transform: capitalize;
        `,
      },
    },
    button: {
      md: css`
        font-family: ${themeVars.text.familyHeading};
        font-weight: 500;
        font-size: ${themeVars.text.sizeBodyMD};
        line-height: ${themeVars.text.lineHeightBodySM};
        letter-spacing: ${themeVars.text.letterSpacingSM};
        text-transform: capitalize;
      `,
      sm: css`
        font-family: ${themeVars.text.familyBody};
        font-weight: 400;
        font-size: ${themeVars.text.sizeBodyMD};
        line-height: ${themeVars.text.lineHeightBodySM};
        letter-spacing: 0;
        text-transform: capitalize;
      `,
      xs: css`
        font-family: ${themeVars.text.familyBody};
        font-weight: 400;
        font-size: ${themeVars.text.sizeBodySM};
        line-height: ${themeVars.text.sizeBodyXS};
        letter-spacing: 0;
        text-transform: capitalize;
      `,
    },
    link: {
      md: css`
        font-family: ${themeVars.text.familyBody};
        font-weight: 400;
        font-size: ${themeVars.text.sizeBodyMD};
        line-height: ${themeVars.text.lineHeightBodySM};
        letter-spacing: 0;
        text-decoration: underline;
        text-decoration-style: solid;
        text-decoration-offset: 24%;
        text-decoration-thickness: 8%;
        text-decoration-skip-ink: auto;
      `,
    },
  },
};
